/*
============================================================
 ArchiveDataSource - Loads archive puzzle data from local files
------------------------------------------------------------
 Implements the data source interface for archived puzzles
 stored in a dedicated subdirectory (VITE_DATA_PATH/archive/)
 useful for historical puzzles and older content.
============================================================
*/

/**
 * Data source for loading archive puzzles from a subdirectory
 * @class ArchiveDataSource
 */
export class ArchiveDataSource {
  /**
   * @param {string} archivePath - Base path to archive data files (e.g., './data/archive')
   */
  constructor(archivePath) {
    this.archivePath = archivePath;
  }

  /**
   * Fetch archive puzzle data from a local JSON file
   * @async
   * @param {string} archiveId - The archive puzzle identifier (without .json extension)
   * @returns {Promise<Object>} The puzzle data object
   * @throws {Error} If file not found or invalid
   */
  async fetch(archiveId) {
    if (!archiveId) {
      throw new Error("Archive ID is required");
    }

    const trimmedId = archiveId.trim();
    const url = `${this.archivePath}/${trimmedId}.json`;
    console.log(`[ArchiveDataSource] Fetching from: ${url}`);

    try {
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: File not found`);
      }

      const data = await response.json();

      // Validate structure
      if (!data || !data.metadata || !data.finmodel) {
        throw new Error("Invalid puzzle data structure");
      }

      // Validate metadata has required fields
      if (!data.metadata.title) {
        throw new Error(
          "Invalid puzzle data structure: metadata.title is required",
        );
      }

      return data;
    } catch (error) {
      console.error(
        `[ArchiveDataSource] Error loading ${archiveId}:`,
        error.message,
      );
      throw error;
    }
  }
}
